import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SettingsField } from '../../../../components/design-system/common/SettingsPanel';
import { config as loadedConfig, useSettingsApi } from '../../../../common/hooks/use-settings-api.hook';

/** Same bounds as the API (pms-config/assistant-chips.ts). */
export const MAX_CHIPS = 12;
export const MAX_CHIP_LENGTH = 40;

/** A chip as it will be stored: spaces collapsed and trimmed, at most MAX_CHIP_LENGTH. */
export const tidyChip = (value: string): string => value.replace(/\s+/g, ' ').trim().slice(0, MAX_CHIP_LENGTH).trim();

/**
 * The vendor's own suggested searches for the design assistant: the chips a
 * shopper taps, each typed into the assistant as a search. Empty means the
 * assistant's built-in suggestions. Vendor config `assistantChips`.
 *
 * Saves on every change (optimistic, put back with an error if the save
 * fails), like the switches beside it.
 */
export const AssistantChipsSetting = () => {
    const { t } = useTranslation();
    const settingsAPI = useSettingsApi();
    const apiRef = useRef(settingsAPI);
    apiRef.current = settingsAPI;

    const [chips, setChips] = useState<string[]>(() => (loadedConfig as any)?.assistantChips ?? []);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const touchedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve()
            .then(() => apiRef.current.findConfig())
            .then((cfg: any) => {
                if (cancelled || touchedRef.current || !cfg || typeof cfg !== 'object') return;
                setChips(Array.isArray(cfg.assistantChips) ? cfg.assistantChips : []);
            })
            .catch(() => {
                // Keep what is shown; editing still saves.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const save = async (next: string[]) => {
        const previous = chips;
        touchedRef.current = true;
        setChips(next);
        setSaving(true);
        try {
            await apiRef.current.updateConfig({ assistantChips: next });
            if (loadedConfig) (loadedConfig as any).assistantChips = next;
        } catch {
            setChips(previous);
            toast.error(t('PRODUCT_PRICING.SETTINGS.SAVE_FAILED', 'Could not save this setting. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const chip = tidyChip(draft);
    const isRepeat = chips.some((c) => c.toLowerCase() === chip.toLowerCase());
    const canAdd = !!chip && !isRepeat && chips.length < MAX_CHIPS && !saving;

    const add = () => {
        if (!canAdd) return;
        setDraft('');
        void save([...chips, chip]);
    };
    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            add();
        }
    };

    return (
        <SettingsField
            label={(
                <span className="inline-flex items-center gap-2">
                    <Sparkles className="size-4 text-muted-foreground" />
                    {t('PRODUCT_PRICING.SETTINGS.ASSISTANT_CHIPS', 'Suggested searches in the design assistant')}
                </span>
            )}
            htmlFor="assistant-chips-input"
            hint={t(
                'PRODUCT_PRICING.SETTINGS.ASSISTANT_CHIPS_HINT',
                'Shoppers tap one to search for it. Use words from your product names, collections or descriptions: a search that finds fewer than two products is not shown. Leave empty for the assistant\'s own suggestions.',
            )}
        >
            {chips.length > 0 && (
                <ul className="mb-2 flex flex-wrap gap-1.5" data-testid="assistant-chips">
                    {chips.map((c) => (
                        <li key={c} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 ps-2.5 pe-1 text-xs text-foreground">
                            {c}
                            <button
                                type="button"
                                onClick={() => void save(chips.filter((x) => x !== c))}
                                disabled={saving}
                                aria-label={t('PRODUCT_PRICING.SETTINGS.ASSISTANT_CHIPS_REMOVE', 'Remove {{chip}}', { chip: c })}
                                className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                                <X className="size-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex gap-2">
                <Input
                    id="assistant-chips-input"
                    value={draft}
                    maxLength={MAX_CHIP_LENGTH}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t('PRODUCT_PRICING.SETTINGS.ASSISTANT_CHIPS_PLACEHOLDER', 'e.g. Hand-knotted')}
                    disabled={chips.length >= MAX_CHIPS}
                />
                <Button type="button" variant="outline" onClick={add} disabled={!canAdd}>
                    {t('PRODUCT_PRICING.SETTINGS.ASSISTANT_CHIPS_ADD', 'Add')}
                </Button>
            </div>
        </SettingsField>
    );
};
